<template>
  <div class="page">
    <div class="roll_back_line flex justify-between">
      <div @click="goBack" class="inline-block flex w-1/3 justify-start items-center go_back pointer">
        <svg width="24" height="24" viewBox="0  0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
          <path d="M14 18L8 12L14 6" stroke="#6c757d" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
        </svg>
        <div class="h-full label_roll_back">Back</div>
      </div>
    </div>
    <div class="title_line flex items-center justify-between w-full px-16">
      <h2>Mobile Route Details</h2>
    </div>

    <div v-if="error_load" class="body px-16">
      <div class="w-full error">Oooops! Something went wrong!</div>
    </div>

    <div v-if="!error_load && loaded_info" class="body">
      <div class="route_details px-16 py-5">
        <div class="text-left bordered rounded p-2">
          <div class="title rounded bg-grey-lighter py-5 px-5">
            <h2>Route details</h2>
          </div>
          <div class="info">
            <div class="info_group flex py-4">
              <div class="name w-1/3">Trip ID</div>
              <div class="value w-2/3 px-4">{{ route_data.trip_id || route_data.id }}</div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Active version</div>
              <div class="value w-2/3 px-4">
                v{{ route_data.active_version_number || selectedVersionNumber }}
                <span v-if="route_data.version_count > 1">
                  ({{ route_data.version_count }} versions)
                </span>
              </div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Driver name</div>
              <div class="value w-2/3 px-4">{{ route_data.driver_name || '-' }}</div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Creation date</div>
              <div class="value w-2/3 px-4">{{ route_data.created_at | moment('DD-MM-YYYY') }}</div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Start time</div>
              <div class="value w-2/3 px-4">{{ route_data.created_at | moment('DD-MM-YYYY HH:mm') }}</div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Route duration</div>
              <div class="value w-2/3 px-4">{{ formatRouteDuration() }}</div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Expected end time</div>
              <div class="value w-2/3 px-4">{{ formatExpectedEndTime() }}</div>
            </div>
            <div class="info_group flex py-4">
              <div class="name w-1/3">Vehicle dimensions</div>
              <div class="value w-2/3 px-4">
                Height {{ formatDim(getDimension('height')) }} m,
                Width {{ formatDim(getDimension('width')) }} m,
                Length {{ formatDim(getDimension('length')) }} m,
                Weight {{ formatDim(getDimension('weight')) }} t
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="map px-16 py-5">
        <div class="flex gap-15">
          <div
            v-if="route_data.versions && route_data.versions.length > 1"
            class="w-3/12 bordered rounded p-4 version-sidebar"
            :style="{ height: mapHeight }"
          >
            <h3 class="text-lg font-medium mb-4 flex-shrink-0">Route versions</h3>
            <div ref="versionList" class="version-list">
              <button
                v-for="(version, index) in route_data.versions"
                :key="version.route_log_id"
                type="button"
                class="version-item w-full text-left py-3 px-3 mb-2 rounded border"
                :class="{
                  'version-item-active': selectedVersionIndex === index,
                  'version-item-inactive': selectedVersionIndex !== index,
                }"
                @click="selectVersion(index)"
              >
                <div class="font-medium">
                  Version {{ version.version_number }}
                  <span v-if="version.is_active" class="text-primary">(active)</span>
                </div>
                <div class="text-sm text-grey-dark">
                  {{ version.created_at | moment('DD-MM-YYYY HH:mm') }}
                </div>
                <div class="text-sm text-grey-dark">
                  {{ version.duration || '-' }}
                </div>
              </button>
            </div>
          </div>

          <div :class="route_data.versions && route_data.versions.length > 1 ? 'w-9/12' : 'w-full'">
            <div v-if="canRenderMap" class="bordered rounded overflow-hidden">
              <HereMap
                ref="mobileMap"
                :routeData="mapRouteData"
                :is_plan="false"
                :map_height="mapHeight"
                :zoom_default="6"
                :routeVersionMode="hasMultipleVersions"
                :selectedVersionIndex="selectedVersionIndex"
              />
            </div>
            <div v-else class="bordered rounded p-6 text-grey-dark">
              No route geometry available for map preview.
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import moment from 'moment';
import HereMap from "@/components/HereMap.vue";

export default {
  name: 'MobileRoute',
  components: {
    HereMap,
  },
  data() {
    return {
      loaded_info: false,
      error_load: false,
      route_data: {},
      selectedVersionIndex: 0,
      mapHeight: '580px',
    };
  },
  computed: {
    hasMultipleVersions() {
      return !!(this.route_data.versions && this.route_data.versions.length > 1);
    },
    mapRouteData() {
      if (this.hasMultipleVersions && this.route_data.map_data) {
        return this.route_data.map_data;
      }
      const selectedVersion =
        this.route_data.versions && this.route_data.versions[this.selectedVersionIndex];
      if (selectedVersion && selectedVersion.map_data) {
        return selectedVersion.map_data;
      }
      return this.route_data.map_data || { routes: [{ sections: [] }] };
    },
    canRenderMap() {
      if (
        !this.mapRouteData ||
        !this.mapRouteData.routes ||
        !this.mapRouteData.routes.length
      ) {
        return false;
      }
      return this.mapRouteData.routes.some(
        (route) => route.sections && route.sections.length,
      );
    },
    selectedVersionNumber() {
      const selectedVersion =
        this.route_data.versions && this.route_data.versions[this.selectedVersionIndex];
      return selectedVersion
        ? selectedVersion.version_number
        : this.route_data.active_version_number || 1;
    },
  },
  methods: {
    goBack() {
      this.$router.push({ path: '/routes', query: { tab: 'mobile' } });
    },
    formatDim(value) {
      if (value === null || value === undefined || value === '') {
        return '-';
      }
      return value;
    },
    formatRouteDuration() {
      const selectedVersion =
        this.route_data.versions && this.route_data.versions[this.selectedVersionIndex];
      if (selectedVersion && selectedVersion.duration) {
        return selectedVersion.duration;
      }
      if (this.route_data && this.route_data.duration) {
        return this.route_data.duration;
      }
      return '-';
    },
    formatExpectedEndTime() {
      const selectedVersion =
        this.route_data.versions && this.route_data.versions[this.selectedVersionIndex];
      const createdAt = selectedVersion
        ? selectedVersion.created_at
        : this.route_data.created_at;
      const durationSeconds = selectedVersion
        ? selectedVersion.duration_seconds
        : this.route_data.duration_seconds;

      if (!createdAt) {
        return '-';
      }
      if (!durationSeconds || durationSeconds <= 0) {
        return '-';
      }
      return moment(createdAt)
        .add(durationSeconds, 'seconds')
        .format('DD-MM-YYYY HH:mm');
    },
    getDimension(key) {
      if (!this.route_data || !this.route_data.dimensions) {
        return null;
      }
      return this.route_data.dimensions[key];
    },
    getStartPoint() {
      if (
        this.route_data &&
        this.route_data.direction_data &&
        this.route_data.direction_data.departure &&
        this.route_data.direction_data.departure.lat !== undefined &&
        this.route_data.direction_data.departure.lon !== undefined
      ) {
        return {
          lat: Number(this.route_data.direction_data.departure.lat),
          lng: Number(this.route_data.direction_data.departure.lon),
        };
      }
      return null;
    },
    focusMapOnStartPoint(retries = 0) {
      const startPoint = this.getStartPoint();
      if (!startPoint) {
        return;
      }

      const mapComponent = this.$refs.mobileMap;
      const map = mapComponent && mapComponent.map ? mapComponent.map : null;
      if (!map) {
        if (retries < 8) {
          setTimeout(() => this.focusMapOnStartPoint(retries + 1), 250);
        }
        return;
      }

      map.setCenter(startPoint);
      map.setZoom(11);
    },
    selectVersion(index) {
      this.selectedVersionIndex = index;
    },
    scrollToSelectedVersion(behavior = 'auto') {
      this.$nextTick(() => {
        const list = this.$refs.versionList;
        if (!list) {
          return;
        }
        const active = list.querySelector('.version-item-active');
        if (active) {
          active.scrollIntoView({ block: 'nearest', behavior });
        }
      });
    },
    setInitialVersionIndex() {
      if (!this.route_data.versions || !this.route_data.versions.length) {
        this.selectedVersionIndex = 0;
        return;
      }

      const activeIndex = this.route_data.versions.findIndex(
        (version) => version.is_active,
      );
      this.selectedVersionIndex = activeIndex >= 0 ? activeIndex : 0;
    },
    async loadRoute() {
      this.loaded_info = false;
      this.error_load = false;
      try {
        const tripId = this.$route.params.id;
        const { data } = await this.$http.getAuth(
          `${this.$http.apiUrl()}/routes-log/trip/${tripId}`,
        );
        this.route_data = data || {};
        this.setInitialVersionIndex();
        this.$nextTick(() => {
          this.focusMapOnStartPoint();
          this.scrollToSelectedVersion();
        });
      } catch (e) {
        try {
          const { data } = await this.$http.getAuth(
            `${this.$http.apiUrl()}/routes-log/${this.$route.params.id}`,
          );
          this.route_data = data || {};
          this.setInitialVersionIndex();
          this.$nextTick(() => {
            this.focusMapOnStartPoint();
            this.scrollToSelectedVersion();
          });
        } catch (fallbackError) {
          console.log(fallbackError);
          this.error_load = true;
        }
      } finally {
        this.loaded_info = true;
      }
    },
  },
  async mounted() {
    await this.loadRoute();
  },
}
</script>

<style scoped>
.version-sidebar {
  display: flex;
  flex-direction: column;
  overflow: hidden;
}

.version-list {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overflow-x: hidden;
  padding-right: 2px;
}

.version-item {
  background: #fff;
  transition: border-color 0.2s ease, background-color 0.2s ease;
}

.version-item-active {
  border-color: #007bff;
  background: #f8fbff;
}

.version-item-inactive {
  border-color: #e2e8f0;
}
</style>
